import React, { useEffect } from 'react';
import { Zap } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { policies } from '../PolicyData';
import { SimulationControls } from './SimulationControls';
import { SimulationTimeline } from './SimulationTimeline';
import { EntitySelection } from './EntitySelection';
import { ExecutionLogs } from './ExecutionLogs';
import { SimulationResults } from './SimulationResults';
import { SettingsDialog } from './SettingsDialog';
import { defaultSimulationSteps } from './simulationUtils';
import { useSimulation } from './useSimulation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useGraphTeams } from '@/hooks/useGraphTeams';
import { cn } from '@/lib/utils';
import { Entity } from './types';

interface PolicyExecutorSimulationProps {
  policyId?: number;
  teamId?: string;
  teamName?: string;
}

export const PolicyExecutorSimulation = ({ policyId, teamId, teamName }: PolicyExecutorSimulationProps) => {
  const policy = policyId ? policies.find(p => p.id === policyId) : policies[0];
  const isMobile = useIsMobile();
  
  // Get teams from the Graph API or mock data
  const { teams, loading } = useGraphTeams();
  
  // Convert Graph teams to entities for simulation
  const entities: Entity[] = React.useMemo(() => {
    if (teams && teams.length > 0) {
      return teams.map(team => ({
        id: team.id,
        name: team.displayName,
        type: 'team',
        status: team.isArchived ? 'inactive' : 'active',
        description: team.description,
        visibility: team.visibility,
        members: team.members,
        compliance: team.compliance,
        lastActivity: team.lastActivity
      }));
    }
    
    // Fallback to default entities if no teams available
    return teamId 
      ? [{ 
          id: teamId, 
          name: teamName || 'Selected Team', 
          type: 'team', 
          status: 'active',
          compliance: 85,
          members: 12,
          lastActivity: '2 hours ago'
        }]
      : defaultEntitiesFallback;
  }, [teams, teamId, teamName]);
  
  const simulationSteps = defaultSimulationSteps;

  const {
    isRunning,
    progress,
    speed,
    currentStep,
    executionLogs,
    selectedEntities,
    showResults,
    simulationCompleted,
    simulationResults,
    setSpeed,
    toggleEntitySelection,
    startSimulation,
    pauseSimulation,
    resetSimulation,
    clearLogs,
    setShowResults
  } = useSimulation(policy || {}, entities, simulationSteps);

  // Pre-select the team if provided
  useEffect(() => {
    if (teamId && !selectedEntities.includes(teamId)) {
      toggleEntitySelection(teamId);
    }
  }, [teamId, selectedEntities, toggleEntitySelection]);
  
  return (
    <div className="flex flex-col h-full rounded-xl border border-border dark:border-slate-700/50 overflow-hidden bg-card dark:bg-card shadow-sm">
      <div className="border-b p-3 md:p-4 flex justify-between items-center bg-muted/30 dark:bg-slate-800/30">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          <h2 className="text-lg font-semibold truncate">{policy?.name || "Policy Executor"}</h2>
          {policy && (
            <Badge variant="outline" className="hidden md:inline-flex ml-2">
              {policy.name}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <SettingsDialog speed={speed} setSpeed={setSpeed} />
        </div>
      </div>
      
      <div className={cn(
        "grid gap-3 md:gap-4 p-3 md:p-4 flex-1 overflow-hidden", 
        isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-7"
      )}>
        {/* Only show entity selection if no specific team is selected */}
        {!teamId && (
          <div className={cn(
            "space-y-4 overflow-y-auto",
            isMobile ? "order-2" : "md:col-span-2"
          )}>
            <EntitySelection 
              entities={entities}
              selectedEntities={selectedEntities}
              toggleEntitySelection={toggleEntitySelection}
              loading={loading}
            />
            
            <SimulationControls 
              isRunning={isRunning}
              onStart={startSimulation}
              onPause={pauseSimulation}
              onReset={resetSimulation}
              progress={progress}
              currentStep={currentStep}
              simulationSteps={simulationSteps}
              simulationCompleted={simulationCompleted}
            />
          </div>
        )}
        
        <div className={cn(
          "flex flex-col gap-4 overflow-hidden",
          isMobile ? "order-1" : teamId ? "md:col-span-7" : "md:col-span-5"
        )}>
          {/* If a team is provided, show simulation controls at the top */}
          {teamId && (
            <SimulationControls 
              isRunning={isRunning}
              onStart={startSimulation}
              onPause={pauseSimulation}
              onReset={resetSimulation}
              progress={progress}
              currentStep={currentStep}
              simulationSteps={simulationSteps}
              simulationCompleted={simulationCompleted}
            />
          )}
          
          <SimulationTimeline 
            steps={simulationSteps}
            currentStep={currentStep}
            progress={progress}
          />
          
          <AnimatePresence>
            {showResults && simulationResults && (
              <SimulationResults 
                results={simulationResults}
                onHide={() => setShowResults(false)}
              />
            )}
          </AnimatePresence>
          
          <ExecutionLogs 
            logs={executionLogs}
            entities={entities}
            clearLogs={clearLogs}
          />
        </div>
      </div>
    </div>
  );
};

// Fallback entities in case no Graph teams are available
const defaultEntitiesFallback: Entity[] = [
  {
    id: 'team-1',
    name: 'Marketing Team',
    type: 'team',
    status: 'active',
    owner: 'John Smith',
    members: 24,
    compliance: 92,
    lastActivity: '1 hour ago'
  },
  {
    id: 'team-2',
    name: 'Finance Department',
    type: 'team',
    status: 'active',
    owner: 'Jane Doe',
    members: 15,
    compliance: 87,
    lastActivity: '3 hours ago'
  },
  {
    id: 'team-3',
    name: 'Product Development',
    type: 'team',
    status: 'active',
    owner: 'David Miller',
    members: 32,
    compliance: 78,
    lastActivity: '30 minutes ago'
  },
  {
    id: 'team-4',
    name: 'HR Team',
    type: 'team',
    status: 'inactive',
    owner: 'Sarah Johnson',
    members: 8,
    compliance: 64,
    lastActivity: '2 days ago'
  },
  {
    id: 'team-5',
    name: 'IT Support',
    type: 'team',
    status: 'active',
    owner: 'Michael Chang',
    members: 12,
    compliance: 95,
    lastActivity: '5 hours ago'
  }
];